/**
 * Procedural SFX using Web Audio API. No asset files — each sound is generated
 * from oscillators + gain envelopes at play time. Cyber-synth aesthetic.
 *
 * The browser blocks AudioContext until first user gesture, so we lazily
 * initialise on first play() and silently ignore plays before that.
 */
import type { ConsumableId } from '@cradle7/shared';

let ctx: AudioContext | null = null;
let unlocked = false;

const SFX_VOLUME_LS_KEY = 'cradle7.sfxVolume';
const MUSIC_VOLUME_LS_KEY = 'cradle7.musicVolume';

function loadInitial(key: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const n = Number(raw);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(0, Math.min(1, n));
  } catch {
    return fallback;
  }
}

let sfxVolume = loadInitial(SFX_VOLUME_LS_KEY, 0.8);
let musicVolume = loadInitial(MUSIC_VOLUME_LS_KEY, 0.6);

export function getSfxVolume(): number { return sfxVolume; }
export function getMusicVolume(): number { return musicVolume; }

export function setSfxVolume(v: number): void {
  sfxVolume = Math.max(0, Math.min(1, v));
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SFX_VOLUME_LS_KEY, String(sfxVolume));
    }
  } catch { /* ignore */ }
}

export function setMusicVolume(v: number): void {
  musicVolume = Math.max(0, Math.min(1, v));
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(MUSIC_VOLUME_LS_KEY, String(musicVolume));
    }
  } catch { /* ignore */ }
  if (ambient) {
    const c = getCtx();
    if (c) {
      const now = c.currentTime;
      ambient.baseGain.gain.cancelScheduledValues(now);
      ambient.baseGain.gain.setTargetAtTime(0.05 * musicVolume, now, 0.05);
      ambient.pulseGain.gain.cancelScheduledValues(now);
      ambient.pulseGain.gain.setTargetAtTime(0.012 * musicVolume, now, 0.05);
      const targetCombat = ambient.combatGain.gain.value > 0 ? 0.022 * musicVolume : 0;
      ambient.combatGain.gain.cancelScheduledValues(now);
      ambient.combatGain.gain.setTargetAtTime(targetCombat, now, 0.05);
    }
  }
}

// ----- Backward-compat aliases -----
/** @deprecated use getSfxVolume + getMusicVolume */
export function getMasterVolume(): number {
  return Math.max(sfxVolume, musicVolume);
}
/** @deprecated use setSfxVolume + setMusicVolume */
export function setMasterVolume(v: number): void {
  setSfxVolume(v);
  setMusicVolume(v);
}

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  if (typeof window === 'undefined') return null;
  const Ctor: typeof AudioContext | undefined =
    (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

/**
 * Call once from a user-gesture event handler (button click, first tap) to
 * unlock audio. Subsequent calls are no-ops.
 */
export function unlockAudio(): void {
  if (unlocked) return;
  const c = getCtx();
  if (!c) return;
  // Touch context: some browsers need resume() too.
  void c.resume();
  unlocked = true;
}

type EnvOpts = {
  /** Volume peak in [0, 1]. */
  gain: number;
  /** Attack time in seconds. */
  attack: number;
  /** Total duration in seconds. */
  duration: number;
};

function playTone(
  freq: number | { from: number; to: number },
  type: OscillatorType,
  env: EnvOpts,
): void {
  const c = getCtx();
  if (!c || !unlocked) return;

  const now = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();

  osc.type = type;
  if (typeof freq === 'number') {
    osc.frequency.value = freq;
  } else {
    osc.frequency.setValueAtTime(freq.from, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, freq.to), now + env.duration);
  }

  const peak = env.gain * sfxVolume;
  gain.gain.setValueAtTime(0, now);
  // exponentialRampToValueAtTime requires positive value; if peak is 0 (muted)
  // we use a very small value and skip the ramp up.
  if (peak > 0.0001) {
    gain.gain.linearRampToValueAtTime(peak, now + env.attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + env.duration);
  } else {
    return; // muted — skip emit entirely
  }

  osc.connect(gain).connect(c.destination);
  osc.start(now);
  osc.stop(now + env.duration + 0.05);
}

/** Short high beep — bullet fired. */
export function sfxShoot(): void {
  playTone(880, 'triangle', { gain: 0.04, attack: 0.005, duration: 0.07 });
}

/** Short blip — bullet hits enemy. */
export function sfxHit(): void {
  playTone(330, 'square', { gain: 0.08, attack: 0.001, duration: 0.06 });
}

/** Descending crunch — enemy dies. */
export function sfxKill(): void {
  playTone({ from: 220, to: 55 }, 'sawtooth', { gain: 0.1, attack: 0.005, duration: 0.22 });
}

/** Harsh low buzz — cat takes damage. */
export function sfxCatHit(): void {
  playTone({ from: 140, to: 80 }, 'square', { gain: 0.12, attack: 0.002, duration: 0.18 });
}

/** Crit hit — high quick double-blip. */
export function sfxCrit(): void {
  playTone(1320, 'square', { gain: 0.08, attack: 0.001, duration: 0.04 });
  setTimeout(() => playTone(1760, 'square', { gain: 0.08, attack: 0.001, duration: 0.05 }), 30);
}

/** Perk pick — rising arpeggio (3 notes). */
export function sfxPerk(): void {
  const notes = [440, 554, 659];
  notes.forEach((f, i) =>
    setTimeout(() => playTone(f, 'sine', { gain: 0.06, attack: 0.005, duration: 0.15 }), i * 70),
  );
}

/** Wave clear — short triumphant tone. */
export function sfxWaveClear(): void {
  playTone({ from: 440, to: 660 }, 'triangle', { gain: 0.08, attack: 0.01, duration: 0.25 });
}

/** Extraction reached — chord. */
export function sfxExtraction(): void {
  playTone(440, 'sine', { gain: 0.06, attack: 0.02, duration: 0.6 });
  setTimeout(() => playTone(554, 'sine', { gain: 0.06, attack: 0.02, duration: 0.55 }), 40);
  setTimeout(() => playTone(659, 'sine', { gain: 0.06, attack: 0.02, duration: 0.5 }), 80);
}

/** KIA — sad descending tone. */
export function sfxDeath(): void {
  playTone({ from: 330, to: 82 }, 'sawtooth', { gain: 0.12, attack: 0.01, duration: 0.8 });
}

// ============================================================
// AMBIENT MUSIC (procedural, looping drone with combat layer)
// ============================================================

type AmbientState = {
  baseOsc: OscillatorNode;
  baseGain: GainNode;
  pulseOsc: OscillatorNode;
  pulseGain: GainNode;
  combatOsc: OscillatorNode;
  combatGain: GainNode;
  lfo: OscillatorNode;
  lfoGain: GainNode;
};

let ambient: AmbientState | null = null;

/**
 * Start the procedural ambient music loop. Idempotent — calling twice is a no-op.
 * Music persists across scene transitions; use setAmbientCombat() to add/remove
 * the tension layer.
 */
export function startAmbient(): void {
  const c = getCtx();
  if (!c || !unlocked || ambient) return;
  const now = c.currentTime;

  // Base drone — low sine wave with a slight detune
  const baseOsc = c.createOscillator();
  baseOsc.type = 'sine';
  baseOsc.frequency.value = 55; // A1
  const baseGain = c.createGain();
  baseGain.gain.setValueAtTime(0, now);
  baseGain.gain.linearRampToValueAtTime(0.05 * musicVolume, now + 2.0); // fade in over 2s
  baseOsc.connect(baseGain).connect(c.destination);
  baseOsc.start(now);

  // Slow LFO modulating the base drone gain (gives a "breathing" pulse)
  const lfo = c.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = 0.13; // ~7.7 sec period
  const lfoGain = c.createGain();
  lfoGain.gain.value = 0.025; // depth of modulation
  lfo.connect(lfoGain).connect(baseGain.gain);
  lfo.start(now);

  // Soft mid-pulse — triangle wave that's mostly silent but gives texture
  const pulseOsc = c.createOscillator();
  pulseOsc.type = 'triangle';
  pulseOsc.frequency.value = 110; // A2
  const pulseGain = c.createGain();
  pulseGain.gain.setValueAtTime(0, now);
  pulseGain.gain.linearRampToValueAtTime(0.012 * musicVolume, now + 3.0);
  pulseOsc.connect(pulseGain).connect(c.destination);
  pulseOsc.start(now);

  // Combat-tension layer — starts muted, controlled by setAmbientCombat
  const combatOsc = c.createOscillator();
  combatOsc.type = 'sawtooth';
  combatOsc.frequency.value = 220; // A3
  const combatGain = c.createGain();
  combatGain.gain.value = 0;
  combatOsc.connect(combatGain).connect(c.destination);
  combatOsc.start(now);

  ambient = { baseOsc, baseGain, pulseOsc, pulseGain, combatOsc, combatGain, lfo, lfoGain };
}

/**
 * Fade out and stop the ambient music. Subsequent startAmbient() will work.
 */
export function stopAmbient(fadeMs = 800): void {
  if (!ambient) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  const tail = fadeMs / 1000;
  const a = ambient;
  // Fade everything down
  a.baseGain.gain.cancelScheduledValues(now);
  a.baseGain.gain.setValueAtTime(a.baseGain.gain.value, now);
  a.baseGain.gain.linearRampToValueAtTime(0, now + tail);
  a.pulseGain.gain.cancelScheduledValues(now);
  a.pulseGain.gain.setValueAtTime(a.pulseGain.gain.value, now);
  a.pulseGain.gain.linearRampToValueAtTime(0, now + tail);
  a.combatGain.gain.cancelScheduledValues(now);
  a.combatGain.gain.setValueAtTime(a.combatGain.gain.value, now);
  a.combatGain.gain.linearRampToValueAtTime(0, now + tail);
  // Stop and disconnect after fade completes
  setTimeout(() => {
    try {
      a.baseOsc.stop();
      a.pulseOsc.stop();
      a.combatOsc.stop();
      a.lfo.stop();
    } catch {
      /* already stopped */
    }
    ambient = null;
  }, fadeMs + 50);
}

/**
 * Engage or disengage the combat-tension layer (sawtooth pulse on top of drone).
 * Smoothly ramps to target gain over ~600ms.
 */
export function setAmbientCombat(active: boolean): void {
  if (!ambient) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  const target = active ? 0.022 * musicVolume : 0;
  ambient.combatGain.gain.cancelScheduledValues(now);
  ambient.combatGain.gain.setValueAtTime(ambient.combatGain.gain.value, now);
  ambient.combatGain.gain.linearRampToValueAtTime(target, now + 0.6);
}

/** Boss arrival — descending sweep with sub-bass thump. */
export function sfxBossArrival(): void {
  playTone({ from: 110, to: 30 }, 'sawtooth', { gain: 0.18, attack: 0.005, duration: 0.7 });
  setTimeout(() => playTone(55, 'square', { gain: 0.14, attack: 0.005, duration: 0.45 }), 120);
}

/** Critical HP alarm — short low pulsing tone played once. */
export function sfxLowHpAlarm(): void {
  playTone(165, 'square', { gain: 0.07, attack: 0.005, duration: 0.18 });
  setTimeout(() => playTone(165, 'square', { gain: 0.07, attack: 0.005, duration: 0.18 }), 220);
}

// ============================================================
// PHASE 2g/2h/2i — consumables, Bastet arc, Daily Challenge
// ============================================================

/**
 * Per-consumable activation sound. Each item has a distinct timbre matched
 * to its mechanic — warm hum for healing, descending sweep for EMP zap,
 * triangle arpeggio for shield uplift, square crunch-zap for nano stim.
 */
export function sfxConsumable(kind: ConsumableId): void {
  switch (kind) {
    case 'med_injector':
      // Warm sine hum — tied to healing
      playTone(440, 'sine', { gain: 0.1, attack: 0.02, duration: 0.3 });
      return;
    case 'emp_grenade':
      // Descending sweep — electric zap
      playTone({ from: 880, to: 220 }, 'sawtooth', { gain: 0.1, attack: 0.005, duration: 0.25 });
      return;
    case 'shield_recharge': {
      // Triangle arpeggio (3 notes ascending) — uplift
      const notes = [660, 831, 988];
      notes.forEach((f, i) =>
        setTimeout(
          () => playTone(f, 'triangle', { gain: 0.08, attack: 0.005, duration: 0.13 }),
          i * 60,
        ),
      );
      return;
    }
    case 'nano_stim':
      // Square double-blip — crunch zap
      playTone(1200, 'square', { gain: 0.07, attack: 0.001, duration: 0.06 });
      setTimeout(
        () => playTone(1200, 'square', { gain: 0.07, attack: 0.001, duration: 0.06 }),
        70,
      );
      return;
  }
}

/** Picking up a consumable — gentle ascending chime. */
export function sfxPickupConsumable(): void {
  playTone({ from: 880, to: 1320 }, 'sine', { gain: 0.06, attack: 0.005, duration: 0.18 });
}

/** Bastet boss arrival — sub-bass thump + dramatic descending sawtooth. */
export function sfxBastetSpawn(): void {
  // Sub-bass thump
  playTone(40, 'sine', { gain: 0.22, attack: 0.005, duration: 0.7 });
  // Heavier descending sawtooth, deeper than aegis_defender
  setTimeout(
    () => playTone({ from: 220, to: 30 }, 'sawtooth', { gain: 0.2, attack: 0.01, duration: 1.0 }),
    60,
  );
  // Mid-low square layer for menace
  setTimeout(
    () => playTone({ from: 110, to: 55 }, 'square', { gain: 0.12, attack: 0.005, duration: 0.6 }),
    250,
  );
}

/** Bastet phase 1→2 transition — resonant gong, ominous slow attack. */
export function sfxBastetPhaseTransition(): void {
  playTone({ from: 220, to: 110 }, 'triangle', { gain: 0.18, attack: 0.12, duration: 0.8 });
  // Low overtone for resonance
  setTimeout(
    () => playTone({ from: 110, to: 55 }, 'sine', { gain: 0.14, attack: 0.1, duration: 0.7 }),
    40,
  );
}

/** Bastet defeated — triumphant ascending chord (3 notes). */
export function sfxBastetDefeated(): void {
  // C5, C#5+, A5 — major-ish triumphant feel
  playTone(440, 'sine', { gain: 0.1, attack: 0.02, duration: 0.6 });
  setTimeout(() => playTone(554, 'sine', { gain: 0.1, attack: 0.02, duration: 0.7 }), 180);
  setTimeout(() => playTone(880, 'sine', { gain: 0.12, attack: 0.02, duration: 0.9 }), 360);
}

/** Codex entry unlock — short "data ping" double-blip. */
export function sfxCodexUnlock(): void {
  playTone(1320, 'sine', { gain: 0.06, attack: 0.002, duration: 0.08 });
  setTimeout(() => playTone(1320, 'sine', { gain: 0.06, attack: 0.002, duration: 0.08 }), 50);
}

/** Challenge accepted — short ascending square. */
export function sfxChallengeStart(): void {
  playTone({ from: 660, to: 880 }, 'square', { gain: 0.08, attack: 0.005, duration: 0.25 });
}

// ============================================================
// PHASE 3l — Survivor mode SFX
// ============================================================

/** Level-up — short ascending two-note chime (minor sixth interval). */
export function sfxLevelUp(): void {
  playTone(660, 'sine', { gain: 0.1, attack: 0.01, duration: 0.18 });
  setTimeout(() => playTone(1047, 'sine', { gain: 0.1, attack: 0.01, duration: 0.22 }), 90);
}

/**
 * XP gem pickup — very short high ping.
 * Throttled: plays at most once every 120 ms to avoid machine-gun effect when
 * vacuuming many gems at once.
 */
let _lastXpGemPlayMs = -Infinity;
export function sfxXpGem(): void {
  const now = Date.now();
  if (now - _lastXpGemPlayMs < 120) return;
  _lastXpGemPlayMs = now;
  playTone(2093, 'triangle', { gain: 0.04, attack: 0.002, duration: 0.06 });
}

/** Hazard puddle spawned — low short warning thud. */
export function sfxHazard(): void {
  playTone({ from: 120, to: 60 }, 'square', { gain: 0.09, attack: 0.003, duration: 0.14 });
}

/** Sector survived — positive confirip (replaces sfxWaveClear for timed sectors). */
export function sfxSectorClear(): void {
  playTone(523, 'triangle', { gain: 0.09, attack: 0.01, duration: 0.18 });
  setTimeout(() => playTone(659, 'triangle', { gain: 0.09, attack: 0.008, duration: 0.22 }), 80);
  setTimeout(() => playTone(784, 'triangle', { gain: 0.1, attack: 0.008, duration: 0.28 }), 160);
}

/**
 * Challenge result chime. If the player earned fishies — triumphant 3-note
 * ascending sine; otherwise neutral 2-note chime (no reward, but flow ends).
 */
export function sfxChallengeResult(awardedFishies: number): void {
  if (awardedFishies > 0) {
    // Triumphant ascending chord — same shape as sfxBastetDefeated but lighter
    playTone(523, 'sine', { gain: 0.09, attack: 0.015, duration: 0.45 });
    setTimeout(() => playTone(659, 'sine', { gain: 0.09, attack: 0.015, duration: 0.5 }), 130);
    setTimeout(() => playTone(988, 'sine', { gain: 0.1, attack: 0.015, duration: 0.6 }), 260);
  } else {
    // Neutral closing chime
    playTone(523, 'sine', { gain: 0.07, attack: 0.015, duration: 0.4 });
    setTimeout(() => playTone(659, 'sine', { gain: 0.07, attack: 0.015, duration: 0.4 }), 140);
  }
}
